import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

export interface InstitutionOption {
  payrollClient_ID: string;
  payrollClient_Name: string;
  institution_ID: string;
  institution_Name: string;
  institution_Code: number;
}

export interface BookingGroupOption {
  booking_Group_ID: string;
  bookingGroup_Name: string;
}

export interface OverviewDefinitionOption {
  overview_Definition_ID: string;
  overviewDefinition_Name: string;
  booking_Group_ID: string;
}

export interface ConsolidationDefinitionOption {
  consolidation_Definition_ID: string;
  consolidationDefinition_Name: string;
  booking_Group_ID: string;
}

export interface JournalOverviewRow {
  journal_PlanningRecord_ID: string;
  institution_Name: string;
  payroll_Period: number;
  payroll_Year: number;
  journal_Type: string;
  changeDateTime: string;
  kindOf_JournalEntry: string; // 'REPLACE' | 'SUPPLEMENT'
  booking_Date: string;
  booking_Description: string;
}

export interface JournalOverviewQuery {
  institutionId: string;
  bookingGroupId: string;
  overviewDefinitionId: string;
  consolidationDefinitionId?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class OverviewJournalEntryService {
  private frontendUrl = environment.apiUrl + '/kbj-frontend/api/v1';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns the institutions used to populate the "Bedrijf" combobox.
   * Returns an empty array when the API responds with 204 No Content.
   */
  getInstitutions(): Observable<InstitutionOption[]> {
    return this.http
      .get<InstitutionOption[] | null>(
        this.frontendUrl + '/SelectionList/Institution',
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => this.emptyOr<InstitutionOption>(err)),
      );
  }

  /**
   * Returns the booking groups used to populate the "Boekingsgroep" combobox.
   * Returns an empty array when the API responds with 204 No Content.
   */
  getBookingGroups(): Observable<BookingGroupOption[]> {
    return this.http
      .get<BookingGroupOption[] | null>(
        this.frontendUrl + '/SelectionList/BookingGroup',
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => this.emptyOr<BookingGroupOption>(err)),
      );
  }

  /**
   * Returns the overview definitions for a booking group ("Overzichtdefinitie").
   * Returns an empty array when the API responds with 204 No Content.
   */
  getOverviewDefinitions(bookingGroupId: string): Observable<OverviewDefinitionOption[]> {
    return this.http
      .get<OverviewDefinitionOption[] | null>(
        this.frontendUrl + '/SelectionList/OverviewDefinition/' + bookingGroupId,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => this.emptyOr<OverviewDefinitionOption>(err)),
      );
  }

  /**
   * Returns the consolidation definitions for a booking group ("Consolidatiedefinitie").
   * Returns an empty array when the API responds with 204 No Content.
   */
  getConsolidationDefinitions(bookingGroupId: string): Observable<ConsolidationDefinitionOption[]> {
    return this.http
      .get<ConsolidationDefinitionOption[] | null>(
        this.frontendUrl + '/SelectionList/ConsolidationDefinition/' + bookingGroupId,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => this.emptyOr<ConsolidationDefinitionOption>(err)),
      );
  }

  /**
   * Returns the journal overview rows for the selected search criteria.
   * Returns an empty array when the API responds with 204 No Content.
   */
  getJournalOverview(query: JournalOverviewQuery): Observable<JournalOverviewRow[]> {
    return this.http
      .get<JournalOverviewRow[] | null>(this.frontendUrl + '/Journal/JournalOverview', {
        ...this.httpOptions,
        params: {
          institutionId: query.institutionId,
          bookingGroupId: query.bookingGroupId,
          overviewDefinitionId: query.overviewDefinitionId,
          consolidationDefinitionId: query.consolidationDefinitionId ?? '',
        },
      })
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => this.emptyOr<JournalOverviewRow>(err)),
      );
  }

  /**
   * Creates a journal file export and triggers a download of the resulting
   * file. The selected journal planning record ids are sent in the request body.
   */
  createJournalFile(
    query: JournalOverviewQuery,
    planningRecordIds: string[],
  ): Observable<void> {
    return this.http
      .post(this.frontendUrl + '/Journal/CreateJournalFile', planningRecordIds, {
        ...this.httpOptions,
        params: {
          institutionId: query.institutionId,
          bookingGroupId: query.bookingGroupId,
          overviewDefinitionId: query.overviewDefinitionId,
          consolidationDefinitionId: query.consolidationDefinitionId ?? '',
          outputFileType: 'xlsx',
        },
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map((response: HttpResponse<Blob>) => {
          this.triggerDownload(response);
        }),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  // ------------------------------------------------------------------------------
  // Helpers

  private triggerDownload(response: HttpResponse<Blob>): void {
    const blob = response.body;
    if (!blob) {
      return;
    }

    const fileName = this.resolveFileName(response.headers.get('content-disposition'));
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }

  private resolveFileName(contentDisposition: string | null): string {
    const fallback = 'Overzicht journaalposten.xlsx';
    if (!contentDisposition) {
      return fallback;
    }

    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1].trim());
    }

    const asciiMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
    if (asciiMatch?.[1]) {
      return asciiMatch[1].trim();
    }

    return fallback;
  }

  private emptyOr<T>(err: HttpErrorResponse): Observable<T[]> {
    if (err.status === 404 || err.status === 204) {
      return of([]);
    }
    return this.handleError(err);
  }

  private handleError(err: HttpErrorResponse): Observable<never> {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
